import {
  ReconciliationSessionItemModel,
  ReconciliationSessionDetailModel,
  ReconciliationTransactionModel,
  ReconciliationTransactionDetailModel,
  ReconciliationStatus,
  ReconciliationChannel,
  ReconciliationMethod,
  ReconciliationTransactionStatus,
  ReconciliationTransactionType,
  PageableModel,
} from "@/models/ReconciliationModel";
import {
  ReconciliationSessionResponse,
  ReconciliationSessionDetailResponse,
  ReconciliationResponse,
  ReconciliationTransaction,
  PageResponseReconciliationSessionResponse,
  PageResponseReconciliationResponse,
  ReconciliationSessionResponseStatusEnum,
  ReconciliationSessionResponseChannelEnum,
  ReconciliationResponseMethodEnum,
  ReconciliationResponseStatusEnum,
  ReconciliationResponseTransactionTypeEnum,
  ReconciliationTransactionStatusEnum,
} from "@/api/generated-sources/kienlong-pay/api";

/**
 * Adapter để transform data từ API response sang Domain Model
 */

/**
 * Map ReconciliationSessionResponseStatusEnum → ReconciliationStatus
 */
function mapReconciliationStatus(
  status?: ReconciliationSessionResponseStatusEnum,
): ReconciliationStatus {
  switch (status) {
    case ReconciliationSessionResponseStatusEnum.Success:
      return ReconciliationStatus.SUCCESS;
    case ReconciliationSessionResponseStatusEnum.Waiting:
      return ReconciliationStatus.WAITING;
    case ReconciliationSessionResponseStatusEnum.Solving:
      return ReconciliationStatus.SOLVING;
    case ReconciliationSessionResponseStatusEnum.Failed:
      return ReconciliationStatus.FAILED;
    default:
      return ReconciliationStatus.WAITING;
  }
}

/**
 * Map ReconciliationSessionResponseChannelEnum → ReconciliationChannel
 */
function mapReconciliationChannel(
  channel?: ReconciliationSessionResponseChannelEnum,
): ReconciliationChannel {
  switch (channel) {
    case ReconciliationSessionResponseChannelEnum.Tcb:
      return ReconciliationChannel.TCB;
    default:
      return ReconciliationChannel.TCB;
  }
}

/**
 * Map ReconciliationResponseMethodEnum → ReconciliationMethod
 */
function mapReconciliationMethod(
  method?: ReconciliationResponseMethodEnum,
): ReconciliationMethod {
  switch (method) {
    case ReconciliationResponseMethodEnum.DiffInfo:
      return ReconciliationMethod.DIFF_INFO;
    case ReconciliationResponseMethodEnum.DiffMoney:
      return ReconciliationMethod.DIFF_MONEY;
    case ReconciliationResponseMethodEnum.DiffStatus:
      return ReconciliationMethod.DIFF_STATUS;
    case ReconciliationResponseMethodEnum.ReconciliationExist:
      return ReconciliationMethod.RECONCILIATION_EXIST;
    case ReconciliationResponseMethodEnum.NotReconciliation:
      return ReconciliationMethod.NOT_RECONCILIATION;
    case ReconciliationResponseMethodEnum.ReconciliationOther:
      return ReconciliationMethod.RECONCILIATION_OTHER;
    case ReconciliationResponseMethodEnum.ReconciliationDone:
      return ReconciliationMethod.RECONCILIATION_DONE;
    default:
      return ReconciliationMethod.NOT_RECONCILIATION;
  }
}

/**
 * Map ReconciliationResponseStatusEnum → ReconciliationTransactionStatus
 */
function mapReconciliationTransactionStatus(
  status?: ReconciliationResponseStatusEnum,
): ReconciliationTransactionStatus {
  switch (status) {
    case ReconciliationResponseStatusEnum.Success:
      return ReconciliationTransactionStatus.SUCCESS;
    case ReconciliationResponseStatusEnum.Waiting:
      return ReconciliationTransactionStatus.WAITING;
    case ReconciliationResponseStatusEnum.Solved:
      return ReconciliationTransactionStatus.SOLVED;
    case ReconciliationResponseStatusEnum.Failed:
      return ReconciliationTransactionStatus.FAILED;
    default:
      return ReconciliationTransactionStatus.WAITING;
  }
}

/**
 * Map ReconciliationResponseTransactionTypeEnum → ReconciliationTransactionType
 */
function mapReconciliationTransactionType(
  transactionType?: ReconciliationResponseTransactionTypeEnum,
): ReconciliationTransactionType | undefined {
  if (!transactionType) return undefined;
  switch (transactionType) {
    case ReconciliationResponseTransactionTypeEnum.Payment:
      return ReconciliationTransactionType.PAYMENT;
    case ReconciliationResponseTransactionTypeEnum.Refund:
      return ReconciliationTransactionType.REFUND;
    default:
      return undefined;
  }
}

/**
 * Map ReconciliationTransaction → ReconciliationTransactionDetailModel
 */
export function mapReconciliationTransactionDetailModel(
  dto: ReconciliationTransaction,
): ReconciliationTransactionDetailModel {
  return {
    txnNumber: dto.txnNumber,
    referenceId: dto.referenceId,
    refundTransactionLogId: dto.refundTransactionLogId,
    amount: dto.amount,
    paymentType: dto.paymentType,
    status: dto.status,
    completeTime: dto.completeTime,
    description: dto.description,
    isUpdate: dto.isUpdate,
    isDelete: dto.isDelete,
  };
}

/**
 * Map ReconciliationResponse → ReconciliationTransactionModel
 */
export function mapReconciliationTransactionModel(
  dto: ReconciliationResponse,
): ReconciliationTransactionModel {
  return {
    id: dto.id || "",
    sessionId: dto.sessionId || "",
    txnNumber: dto.txnNumber || "",
    amount: dto.amount || 0,
    diffAmount: dto.diffAmount || 0,
    method: mapReconciliationMethod(dto.method),
    status: mapReconciliationTransactionStatus(dto.status),
    reconciliationDate: dto.reconciliationDate || "",
    appConfirmed: dto.appConfirmed || false,
    appReconTransaction: dto.appReconTransaction
      ? mapReconciliationTransactionDetailModel(dto.appReconTransaction)
      : undefined,
    bankReconTransaction: dto.bankReconTransaction
      ? mapReconciliationTransactionDetailModel(dto.bankReconTransaction)
      : undefined,
    funTransId: dto.funTransId,
    transactionType: mapReconciliationTransactionType(dto.transactionType),
  };
}

/**
 * Map ReconciliationSessionResponse → ReconciliationSessionItemModel
 */
export function mapReconciliationSessionItemModel(
  dto: ReconciliationSessionResponse,
): ReconciliationSessionItemModel {
  return {
    id: dto.id || "",
    channel: mapReconciliationChannel(dto.channel),
    date: dto.date || "",
    totalTransactions: dto.totalTransactions || 0,
    totalMismatchedTransactions: dto.totalMismatchedTransactions || 0,
    status: mapReconciliationStatus(dto.status),
  };
}

/**
 * Map ReconciliationSessionDetailResponse → ReconciliationSessionDetailModel
 */
export function mapReconciliationSessionDetailModel(
  dto: ReconciliationSessionDetailResponse,
): ReconciliationSessionDetailModel {
  const base: ReconciliationSessionItemModel = {
    id: dto.id || "",
    channel: mapReconciliationChannel(dto.channel),
    date: dto.date || "",
    totalTransactions: dto.totalTransactions || 0,
    totalMismatchedTransactions: dto.totalMismatchedTransactions || 0,
    status: mapReconciliationStatus(dto.status),
  };

  return {
    ...base,
    totalMatchedTransactions: dto.totalMatchedTransactions || 0,
  };
}

/**
 * Map PageResponseReconciliationSessionResponse → PageableModel<ReconciliationSessionItemModel>
 */
export function mapReconciliationSessionsListResponse(
  dto: PageResponseReconciliationSessionResponse,
): PageableModel<ReconciliationSessionItemModel> {
  return {
    items: (dto.items || []).map(mapReconciliationSessionItemModel),
    pageNumber: dto.pageNumber || 0,
    pageSize: dto.pageSize || 10,
    totalSize: dto.totalSize || 0,
    totalPage: dto.totalPage || 0,
  };
}

/**
 * Map PageResponseReconciliationResponse → PageableModel<ReconciliationTransactionModel>
 */
export function mapReconciliationTransactionsListResponse(
  dto: PageResponseReconciliationResponse,
): PageableModel<ReconciliationTransactionModel> {
  return {
    items: (dto.items || []).map(mapReconciliationTransactionModel),
    pageNumber: dto.pageNumber || 0,
    pageSize: dto.pageSize || 10,
    totalSize: dto.totalSize || 0,
    totalPage: dto.totalPage || 0,
  };
}
